import React, { useState, useEffect } from 'react';
import '../../styles/modal.css';
import axios from 'axios';
import axiosInstance from '../../api/axiosInstance';
import { stepsToText } from './testCaseStepUtils';

const EditTestCaseModal = ({ isOpen, onClose, onSaveTestCase, testCase }) => {
  
  const [testCaseName, setTestCaseName] = useState('');
  const [testCaseDescription, setTestCaseDescription] = useState('');
  const [testCasePriority, setTestCasePriority] = useState('medium');
  const [testCaseStatus, setTestCaseStatus] = useState('pending');
  const [testCaseSteps, setTestCaseSteps] = useState('');
  const [testCaseExpectedResult, setTestCaseExpectedResult] = useState('');
  const [environment, setEnvironment] = useState('');
  const [browser, setBrowser] = useState('');
  const [device, setDevice] = useState('');
  const [testSuites, setTestSuites] = useState([]);
  const [selectedSuiteId, setSelectedSuiteId] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [suitesLoading, setSuitesLoading] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_BASE_URL;

  const getUserId = () => {
    const authDataString = localStorage.getItem("user");
    if (authDataString) {
      try {
        const authData = JSON.parse(authDataString);
        return authData.user_id || 0;
      } catch (error) {
        console.error("Error parsing auth data:", error);
        return 0;
      }
    }
    return 0;
  };

  const getAuthHeader = () => {
    const authDataString = localStorage.getItem("user");
    if (authDataString) {
      try {
        const authData = JSON.parse(authDataString);
        return {
          token: authData.accessToken,
          "Content-Type": "application/json",
        };
      } catch (error) {
        console.error("Error parsing auth data:", error);
        return {
          "Content-Type": "application/json",
        };
      }
    }
    return {
      "Content-Type": "application/json",
    };
  };

  useEffect(() => {
    const fetchTestSuites = async () => {
      if (!testCase || !testCase.project_id) {
        console.log("No project ID, skipping fetch");
        setTestSuites([]);
        setSelectedSuiteId('');
        return;
      }
      
      try {
        setSuitesLoading(true);
        setError(null);
        const headers = getAuthHeader();
        
        console.log(`Fetching test suites for project: ${testCase.project_id}`);
        
        const response = await axios.get(
          `${API_BASE_URL}/test_suite/list/${testCase.project_id}`,
          {
            params: {
              page: 1,
              page_size: 100
            },
            headers: headers,
          }
        );
        // const response = await axiosInstance.get(
        //   `/test_suite/list/${testCase.project_id}`,
        //   {
        //     params: {
        //       page: 1,
        //       page_size: 100
        //     }
        //             }
        // );

        console.log("Test suites API response:", response);
        

        let suites = [];
        if (response.data && response.data.test_suites && Array.isArray(response.data.test_suites)) {
          suites = response.data.test_suites;
          console.log("Found test suites in response.data.test_suites:", suites.length);
        } else {
          console.log("No test suites found in expected response format");
        }

        console.log("Extracted suites:", suites);
        
        setTestSuites(suites);
        
   
        if (testCase.suite_id) {
          setSelectedSuiteId(testCase.suite_id.toString());
        } else if (suites.length > 0) {
          setSelectedSuiteId(suites[0].suite_id.toString());
        }
        
      } catch (error) {
        console.error("Error fetching test suites:", error);
        console.error("Error details:", error.response?.data || error.message);
        setError("Failed to load test suites. Please try again.");
        setTestSuites([]);
      } finally {
        setSuitesLoading(false);
      }
    };

    if (isOpen && testCase && testCase.project_id) {
      console.log("Conditions met, fetching test suites");
      fetchTestSuites();
    }
  }, [isOpen, testCase, API_BASE_URL]);

  useEffect(() => {
    if (testCase) {
       setTestCaseName(testCase.name || '');
      setTestCaseDescription(testCase.description || '');
      setTestCasePriority(testCase.priority || 'medium');
      setTestCaseStatus(testCase.status || 'pending');
      
      if (testCase.steps) {
        setTestCaseSteps(stepsToText(testCase.steps.step_description));
        setTestCaseExpectedResult(stepsToText(testCase.steps.expected_result));
      } else {
        setTestCaseSteps('');
        setTestCaseExpectedResult('');
      }
      
      setEnvironment(testCase.environment || '');
      setBrowser(testCase.browser || '');
      setDevice(testCase.device || '');
      
      if (testCase.suite_id) {
        setSelectedSuiteId(testCase.suite_id.toString());
      }
    }
  }, [testCase]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!testCaseName.trim()) {
      setError('Test case name is required');
      return;
    }

    if (!selectedSuiteId) {
      setError('Please select a test suite');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const headers = getAuthHeader();
      const userId = getUserId();
      
  
      const stepDescriptions = testCaseSteps.split('\n').filter(step => step.trim() !== '');
      const expectedResults = testCaseExpectedResult.split('\n').filter(result => result.trim() !== '');
      
     
      const payload = {
        name: testCaseName.trim(),
        suite_id: parseInt(selectedSuiteId),
        priority: testCasePriority,
        status: testCaseStatus,
        description: testCaseDescription.trim(),
        automation_status: testCase.automation_status || "not_automated",
       execution_time: String(testCase.execution_time || "0"),
        environment: environment.trim(),
        browser: browser.trim(),
        device: device.trim(),
        tags: testCase.tags || "",
        attachments: testCase.attachments || "",
        created_by: parseInt(userId),
        steps: {
          step_description: stepDescriptions,
          expected_result: expectedResults,
          prerequisites: testCase.steps?.prerequisites || [],
          additional_notes: testCase.steps?.additional_notes || []
        }
      };
      
      
      
  
      const response = await axios.put(
        `${API_BASE_URL}/test_case/update_test_case?test_case_id=${testCase.id || testCase?.test_case_id}`,
        payload,
        {
          headers: headers,
        }
      );
      
      if (response.status === 200) {
      
        const updatedTestCase = {
          ...testCase,
          name: testCaseName.trim(),
          description: testCaseDescription.trim(),
          priority: testCasePriority,
          status: testCaseStatus,
          steps: {
            step_description: stepDescriptions,
            expected_result: expectedResults,
            prerequisites: testCase.steps?.prerequisites || [],
            additional_notes: testCase.steps?.additional_notes || []
          },
          environment: environment.trim(),
          browser: browser.trim(),
          device: device.trim(),
          suite_id: parseInt(selectedSuiteId),
          suite_name: testSuites.find(suite => suite.suite_id === parseInt(selectedSuiteId))?.suite_name || testCase.suite_name,
          updated_at: new Date().toISOString()
        };
        
        // Call the onSaveTestCase callback with the updated test case
        onSaveTestCase(updatedTestCase);
        
        // Close modal
        onClose();
      } else {
        setError('Failed to update test case. Please try again.');
      }
    } catch (error) {
      console.error("Error updating test case:", error);
      console.error("Error response:", error.response?.data);
      
      if (error.response && error.response.status === 401) {
        setError('Authentication failed. Please log in again.');
      } else if (error.response && error.response.data) {
        if (error.response.data.detail === "Test suite not found.") {
          setError('The selected test suite was not found. Please select a different test suite.');
        } else if (error.response.data.detail) {
          setError(`Error: ${error.response.data.detail}`);
        } else if (Array.isArray(error.response.data)) {
          const errorMessages = error.response.data.map(err => 
            `${err.loc.join('.')}: ${err.msg}`
          );
          setError(`Validation errors: ${errorMessages.join(', ')}`);
        } else {
          setError('Failed to update test case. Please try again.');
        }
      } else {
        setError('Failed to update test case. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !testCase) return null;

  return (
    <>
      <div data-rt-modal-overlay className="modal-overlay" onClick={onClose}></div>
      <div className="modal edit-test-case-modal" style={{
        maxWidth: "800px",
        width: "90%",
        maxHeight: "90vh",
        borderRadius: "8px",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
        display: "flex",
        flexDirection: "column"
      }}>
        <div className="modal-header" style={{
          borderBottom: "1px solid #e9ecef",
          padding: "16px 24px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "#f8f9fa"
        }}>
          <h2 style={{ margin: "0", fontSize: "1.4rem", fontWeight: "600", color: "#212529" }}>Edit Test Case</h2>
          <button className="modal-close" onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer" }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div className="modal-body" style={{ padding: "24px", maxHeight: "60vh", overflowY: "auto" }}>
          <form className="edit-test-case-form" onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div className="form-group" style={{ marginBottom: "16px" }}>
              <label htmlFor="test-case-name" style={{ display: "block", marginBottom: "8px", fontWeight: "500", color: "#212529" }}>Test Case Name *</label>
              <input
                type="text"
                id="test-case-name"
                placeholder="Enter test case name"
                className="form-input"
                value={testCaseName}
                onChange={(e) => setTestCaseName(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #ced4da",
                  borderRadius: "4px",
                  fontSize: "0.9rem",
                  transition: "border-color 0.2s ease"
                }}
                required
              />
            </div>
            
           
            
            <div className="form-group" style={{ marginBottom: "16px" }}>
              <label htmlFor="test-case-description" style={{ display: "block", marginBottom: "8px", fontWeight: "500", color: "#212529" }}>Description</label>
              <textarea
                id="test-case-description"
                placeholder="Enter test case description"
                className="form-input"
                rows="3"
                value={testCaseDescription}
                onChange={(e) => setTestCaseDescription(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #ced4da",
                  borderRadius: "4px",
                  fontSize: "0.9rem",
                  resize: "vertical",
                  minHeight: "80px",
                  fontFamily: "inherit"
                }}
              ></textarea>
            </div>
            
            <div className="form-row" style={{ display: "flex", gap: "16px", marginBottom: "16px" }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label htmlFor="test-case-priority" style={{ display: "block", marginBottom: "8px", fontWeight: "500", color: "#212529" }}>Priority</label>
                <select
                  id="test-case-priority"
                  className="form-input"
                  value={testCasePriority}
                  onChange={(e) => setTestCasePriority(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: "1px solid #ced4da",
                    borderRadius: "4px",
                    fontSize: "0.9rem",
                    backgroundColor: "white"
                  }}
                >
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
              
              <div className="form-group" style={{ flex: 1 }}>
                <label htmlFor="test-case-status" style={{ display: "block", marginBottom: "8px", fontWeight: "500", color: "#212529" }}>Status</label>
                <select
                  id="test-case-status"
                  className="form-input"
                  value={testCaseStatus}
                  onChange={(e) => setTestCaseStatus(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: "1px solid #ced4da",
                    borderRadius: "4px",
                    fontSize: "0.9rem",
                    backgroundColor: "white"
                  }}
                >
                  <option value="pending">Pending</option>
                  <option value="passed">Passed</option>
                  <option value="failed">Failed</option>
                  <option value="blocked">Blocked</option>
                </select>
              </div>
            </div>
            
            <div className="form-row" style={{ display: "flex", gap: "16px", marginBottom: "16px" }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label htmlFor="test-case-environment" style={{ display: "block", marginBottom: "8px", fontWeight: "500", color: "#212529" }}>Environment</label>
                <input
                  type="text"
                  id="test-case-environment"
                  placeholder="e.g., Production, Staging"
                  className="form-input"
                  value={environment}
                  onChange={(e) => setEnvironment(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: "1px solid #ced4da",
                    borderRadius: "4px",
                    fontSize: "0.9rem"
                  }}
                />
              </div>
              
              <div className="form-group" style={{ flex: 1 }}>
                <label htmlFor="test-case-browser" style={{ display: "block", marginBottom: "8px", fontWeight: "500", color: "#212529" }}>Browser</label>
                <input
                  type="text"
                  id="test-case-browser"
                  placeholder="e.g., Chrome, Firefox"
                  className="form-input"
                  value={browser}
                  onChange={(e) => setBrowser(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: "1px solid #ced4da",
                    borderRadius: "4px",
                    fontSize: "0.9rem"
                  }}
                />
              </div>
            </div>
            
            <div className="form-group" style={{ marginBottom: "16px" }}>
              <label htmlFor="test-case-device" style={{ display: "block", marginBottom: "8px", fontWeight: "500", color: "#212529" }}>Device</label>
              <input
                type="text"
                id="test-case-device"
                placeholder="e.g., iPhone 12, Android Tablet"
                className="form-input"
                value={device}
                onChange={(e) => setDevice(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #ced4da",
                  borderRadius: "4px",
                  fontSize: "0.9rem"
                }}
              />
            </div>
            
            <div className="form-group" style={{ marginBottom: "16px" }}>
              <label htmlFor="test-case-steps" style={{ display: "block", marginBottom: "8px", fontWeight: "500", color: "#212529" }}>Test Steps</label>
              <textarea
                id="test-case-steps"
                placeholder="Enter test steps (one per line)"
                className="form-input"
                rows="5"
                value={testCaseSteps}
                onChange={(e) => setTestCaseSteps(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #ced4da",
                  borderRadius: "4px",
                  fontSize: "0.9rem",
                  resize: "vertical",
                  minHeight: "120px",
                  fontFamily: "inherit",
                  lineHeight: "1.5"
                }}
              ></textarea>
              <div className="form-help" style={{ fontSize: "0.8rem", color: "#6c757d", marginTop: "4px" }}>Enter each step on a separate line</div>
            </div>
            
            <div className="form-group" style={{ marginBottom: "16px" }}>
              <label htmlFor="test-case-expected" style={{ display: "block", marginBottom: "8px", fontWeight: "500", color: "#212529" }}>Expected Result</label>
              <textarea
                id="test-case-expected"
                placeholder="Enter expected result"
                className="form-input"
                rows="3"
                value={testCaseExpectedResult}
                onChange={(e) => setTestCaseExpectedResult(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #ced4da",
                  borderRadius: "4px",
                  fontSize: "0.9rem",
                  resize: "vertical",
                  minHeight: "80px",
                  fontFamily: "inherit"
                }}
              ></textarea>
              <div className="form-help" style={{ fontSize: "0.8rem", color: "#6c757d", marginTop: "4px" }}>Enter each expected result on a separate line</div>
            </div>
            
            {error && <div className="form-error" style={{ color: "#dc3545", fontSize: "0.9rem", marginTop: "8px", padding: "8px 12px", backgroundColor: "#f8d7da", borderRadius: "4px" }}>{error}</div>}
          </form>
        </div>
        <div className="modal-footer" style={{
          display: "flex",
          justifyContent: "flex-end",
          padding: "16px 24px",
          borderTop: "1px solid #e9ecef",
          backgroundColor: "#f8f9fa",
          gap: "12px"
        }}>
          <button
            className="btn btn-outline"
            onClick={onClose}
            disabled={loading}
            style={{
              padding: "8px 16px",
              backgroundColor: "white",
              border: "1px solid #ced4da",
              borderRadius: "4px",
              fontSize: "0.9rem",
              fontWeight: "500",
              cursor: "pointer",
              opacity: loading ? 0.6 : 1
            }}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={loading || testSuites.length === 0 || !selectedSuiteId}
            style={{
              padding: "8px 16px",
              backgroundColor: "#007bff",
              color: "white",
              border: "1px solid #007bff",
              borderRadius: "4px",
              fontSize: "0.9rem",
              fontWeight: "500",
              cursor: "pointer",
              opacity: (loading || testSuites.length === 0 || !selectedSuiteId) ? 0.6 : 1
            }}
          >
            {loading ? 'Updating...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </>
  );
};

export default EditTestCaseModal;